import React, { useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Grid2,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CollapsibleFormElement from "../../common/collapsible-form-element";
import { Monogram } from "../../../pages/playground/types";
import { v4 as uuidv4 } from "uuid";
import { TIGRIS_DATA_PUBLIC_BUCKET_URL } from "../../../configs";

type MonogramsProps = {
  monograms: Monogram[];
  onChange: (monograms: Monogram[]) => void;
  validationErrors?: Record<string, string>;
};

export const Monograms: React.FC<MonogramsProps> = ({
  monograms = [],
  onChange,
  validationErrors = {},
}) => {
  // File input refs for each monogram
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Add a new empty monogram
  const addMonogram = () => {
    // Generate a UUID for frontend tracking to match backend expectations
    // This ensures compatibility with the backend process_monograms method
    // which expects UUIDs for all monogram IDs
    const newMonogram: Monogram = {
      id: uuidv4(), // Use proper UUID format to match backend
      imagePath: "",
      text: "",
    };

    onChange([...monograms, newMonogram]);
  };

  // Update a monogram property
  const updateMonogram = (id: string, field: keyof Monogram, value: string) => {
    const updatedMonograms = monograms.map((monogram) =>
      monogram.id === id ? { ...monogram, [field]: value } : monogram
    );

    onChange(updatedMonograms);
  };

  // Remove a monogram
  const removeMonogram = (id: string) => {
    // Filter out the monogram with the matching id
    // The backend will detect that this monogram is missing and handle deletion
    // This works with our updated process_monograms method which checks
    // for missing IDs in the existing_monograms list and deletes associated S3 images
    const updatedMonograms = monograms.filter((monogram) => monogram.id !== id);

    onChange(updatedMonograms);
  };

  // Handle image upload
  const handleImageUpload = (id: string, file: File) => {
    // Convert file to base64 data URL format for backend processing
    const reader = new FileReader();

    reader.onload = (e) => {
      if (e.target && e.target.result) {
        const base64DataUrl = e.target.result.toString();

        // Find the existing monogram and get its text
        const existingMonogram = monograms.find((m) => m.id === id);
        const text = existingMonogram ? existingMonogram.text : "";

        // Instead of updating the existing monogram, create a new one with a new ID
        // This ensures the backend will treat this as a deletion of the old monogram
        // (triggering S3 image deletion) and creation of a new one
        const newMonogram: Monogram = {
          id: uuidv4(), // Generate a new UUID
          imagePath: base64DataUrl,
          text: text, // Preserve the existing text
        };

        // Replace the old monogram with the new one
        const updatedMonograms = monograms.map((monogram) =>
          monogram.id === id ? newMonogram : monogram
        );

        onChange(updatedMonograms);
      }
    };

    reader.readAsDataURL(file);
  };

  // Trigger file input click
  const triggerFileInput = (id: string) => {
    // Safety check to ensure id exists and is a valid key
    if (id && fileInputRefs.current[id]) {
      fileInputRefs.current[id]?.click();
    }
  };

  // Check if there are validation errors
  const hasErrors = Object.keys(validationErrors).some((key) =>
    key.startsWith("monograms")
  );

  return (
    <CollapsibleFormElement title="Monograms" hasError={hasErrors}>
      {monograms.length > 0 ? (
        <Grid2 container spacing={2}>
          {monograms.map((monogram, index) => (
            <Grid2 key={monogram.id} size={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Grid2 container spacing={1}>
                  {/* Image at the top */}
                  <Grid2 size={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: 150,
                          border: "1px dashed",
                          borderColor: "divider",
                          borderRadius: 1,
                          p: 1,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        onClick={() => triggerFileInput(monogram.id)}
                      >
                        {monogram.imagePath ? (
                          <Box
                            component="img"
                            // The image path can be either:
                            // 1. A data URL from a new upload (starts with 'data:')
                            // 2. A server path from previously saved monogram
                            src={
                              String(monogram.imagePath).includes("data:")
                                ? monogram.imagePath
                                : `${TIGRIS_DATA_PUBLIC_BUCKET_URL}/${monogram.imagePath}`
                            }
                            alt={monogram.text || "Monogram image"}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: 140,
                              borderRadius: 1,
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <Box sx={{ textAlign: "center" }}>
                            <CloudUploadIcon fontSize="large" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Click to upload image
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <input
                        ref={(el) => {
                          fileInputRefs.current[monogram.id] = el;
                        }}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleImageUpload(monogram.id, e.target.files[0]);
                          }
                        }}
                        style={{ display: "none" }}
                      />
                      {!!validationErrors[`monograms.${index}.imagePath`] && (
                        <Typography variant="caption" color="error">
                          {validationErrors[`monograms.${index}.imagePath`]}
                        </Typography>
                      )}
                    </Box>
                  </Grid2>

                  {/* Text field with delete button to the left */}
                  <Grid2 size={12} sx={{ mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeMonogram(monogram.id)}
                        aria-label="Remove monogram"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>

                      <TextField
                        fullWidth
                        label="Monogram Text"
                        size="small"
                        value={monogram.text}
                        onChange={(e) =>
                          updateMonogram(monogram.id, "text", e.target.value)
                        }
                        error={!!validationErrors[`monograms.${index}.text`]}
                        helperText={validationErrors[`monograms.${index}.text`]}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>
          ))}
        </Grid2>
      ) : (
        <Box sx={{ mb: 2, py: 2, textAlign: "center" }}>
          <Typography color="text.secondary">
            No monograms added
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addMonogram}
          size="small"
        >
          Add Monogram
        </Button>
      </Box>
    </CollapsibleFormElement>
  );
};

export default Monograms;
